import type { StoredMergeRefusalCode } from '@developerz/domain';
import { A } from '@solidjs/router';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  APPROVED_PR_MERGEABLE_LABEL_KEY,
  APPROVED_PR_REFUSAL_LABEL_KEY,
  type ApprovedPrMergeable,
  type ApprovedPrRow,
} from '../lib/approved-prs';
import { orgHref } from '../lib/org';
import { absolute, since } from '../lib/time';
import { DataTable, type DataTableColumn } from './DataTable';

// Approved open PRs (the account-wide list of PRs a human approved that are
// still open): TARGET · PULL REQUEST · WAITING · STATE. Presenter — the rows
// arrive from `GET /v1/approved-prs` (`lib/approved-prs.ts`) and the Pending
// route renders them.
//
// NO BUTTONS. This list decides nothing: the approval is already given and the
// merge gate owns the rest. A row here is a STATUS, not a task.
//
// THE STATE CELL HAS TWO ARMS, AND THE ARM IS THE ROW'S OWN STORED FACT. A row
// whose stored state PROVES a refusal (the approval is pinned to an older head,
// or GitHub is holding the merge) names that refusal. A row that proves neither
// shows what it is WAITING for instead: the CI state, GitHub's mergeability and
// whether the merge queue is armed. The two are never mixed — naming a refusal
// the row does not prove would be a guess.
//
// A REFUSAL CODE THIS BUILD HAS NO LABEL FOR READS AS THE RAW CODE, never as a
// bracket. The server may publish a code this build predates, and `t(undefined)`
// would render `⟦undefined⟧` on the one screen whose whole job is saying what a
// PR is waiting for. The mergeability arm below already takes that posture for
// an unreadable word, and the fleet timeline's `kindLabel` is the house
// precedent for the raw fallback.
//
// THE WAITING CELL COUNTS FROM THE APPROVAL (`approved_iso`), because that is
// when this list's clock started: a PR approved an hour ago has been waiting an
// hour, whatever the PR's own age is. A row whose approval instant was never
// recorded falls back to the PR's open instant — the age it has been open, which
// is the only age the row can state.

/** "owner/repo#294" — the PR the row is about. */
function ref(row: ApprovedPrRow): string {
  return `${row.repo_full_name}#${row.github_number}`;
}

/** "octocat · CI passing" — drops the author when unknown. */
function facts(row: ApprovedPrRow): string {
  return [row.author_login, t('approved_prs.ci', { status: row.ci_status })]
    .filter((p): p is string => p !== null)
    .join(' · ');
}

/**
 * The refusal's label — the RAW CODE when this build has no label for it.
 *
 * `Object.hasOwn`, like the mergeability arm beside it: a code the server
 * publishes and this build does not know is not a miss to bracket, it is a word
 * to print.
 */
function refusalLabel(value: string): string {
  if (!Object.hasOwn(APPROVED_PR_REFUSAL_LABEL_KEY, value)) return value;
  return t(APPROVED_PR_REFUSAL_LABEL_KEY[value as StoredMergeRefusalCode]);
}

/** GitHub's mergeability as a label; a word this build does not know reads as unknown. */
function mergeabilityLabel(value: string | null): string {
  const key =
    value !== null && Object.hasOwn(APPROVED_PR_MERGEABLE_LABEL_KEY, value)
      ? APPROVED_PR_MERGEABLE_LABEL_KEY[value as ApprovedPrMergeable]
      : 'approved_prs.mergeability_unknown';
  return t(key);
}

/** What the row is waiting for, when nothing refuses it. */
function waitingState(row: ApprovedPrRow): string {
  return [
    t('approved_prs.ci', { status: row.ci_status }),
    mergeabilityLabel(row.mergeable),
    t(row.auto_merge ? 'approved_prs.queue_armed' : 'approved_prs.queue_idle'),
  ].join(' · ');
}

/** The instant the WAITING cell counts from: the approval, else the open. */
function waitedSince(row: ApprovedPrRow): string {
  return row.approved_iso ?? row.opened_iso;
}

export type ApprovedPrsListProps = {
  readonly rows: readonly ApprovedPrRow[];
};

export function ApprovedPrsList(props: ApprovedPrsListProps): JSX.Element {
  const columns: readonly DataTableColumn<ApprovedPrRow>[] = [
    {
      key: 'target',
      header: t('approved_prs.col_target'),
      mono: true,
      rowHeader: true,
      cell: (row) => (
        <A
          class="pending-actions__target"
          href={orgHref(`/repos/${encodeURIComponent(row.repo_full_name)}/pending`)}
        >
          {ref(row)}
        </A>
      ),
    },
    {
      key: 'pr',
      header: t('approved_prs.col_pr'),
      cell: (row) => (
        <>
          <span class="pending-actions__title">
            {row.title ?? t('overview.approval_title', { number: row.github_number })}
          </span>
          <span class="pending-actions__facts">{facts(row)}</span>
        </>
      ),
    },
    {
      key: 'waiting',
      header: t('approved_prs.col_waiting'),
      mono: true,
      align: 'end',
      cell: (row) => {
        const waited = waitedSince(row);
        return (
          <time datetime={waited} title={absolute(waited)}>
            {since(waited)}
          </time>
        );
      },
    },
    {
      key: 'state',
      header: t('approved_prs.col_state'),
      cell: (row) => (
        <Show
          when={row.merge_refusal_code}
          fallback={<span class="pending-actions__facts">{waitingState(row)}</span>}
        >
          {(code) => (
            <>
              <span class="pending-actions__title">{refusalLabel(code())}</span>
              {/* GitHub's own sentence about its block — the one place the
                  stored row says WHO is holding the merge. */}
              <Show when={row.merge_block_reason}>
                <span class="pending-actions__hint">{row.merge_block_reason}</span>
              </Show>
            </>
          )}
        </Show>
      ),
    },
  ];
  return (
    <DataTable
      label={t('approved_prs.title')}
      class="pending-actions"
      columns={columns}
      rows={props.rows}
      rowKey={(row) => row.id}
    />
  );
}
